/**
 * The rules behind the design page that can be read and tested without rendering it.
 *
 * <p>
 * <strong>Photographs are staged, not sent, until Save.</strong> A new design has no id until its
 * insert has run, and an upload needs one — so the page holds the chosen files here, and Save writes
 * the design, then sends them in order. Anything that fails stays staged with its reason, so the next
 * Save sends only what is still missing.
 * </p>
 *
 * <p>
 * The limits mirror the server's media policy (`MediaOptions`): the type allow-lists, 10 MB a
 * photograph, 200 MB a clip, twenty files a design. The server still refuses; mirroring it means a
 * file that cannot go up is turned away when it is chosen, not after the design has been saved
 * without it.
 * </p>
 */

export const imageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;
export const clipTypes = ['video/mp4', 'video/webm', 'video/quicktime'] as const;

export const maxImageBytes = 10 * 1024 * 1024;
export const maxClipBytes = 200 * 1024 * 1024;
export const maxFilesPerDesign = 20;

/** What the file picker offers. */
export const acceptedTypes = [...imageTypes, ...clipTypes].join(',');

/** One chosen file waiting for Save, with the reason its last attempt failed if it did. */
export interface StagedFile {
  id: string;
  file: File;
  error: string | null;
}

/** The parts of a `File` these rules read, so tests need no real file. */
type FileLike = Pick<File, 'name' | 'type' | 'size'>;

/** A photograph rather than a clip — only a photograph can be a design's main picture. */
export function isImage(file: Pick<File, 'type'>): boolean {
  return (imageTypes as readonly string[]).includes(file.type.toLowerCase());
}

/** Why this file cannot be added, or null when it can. */
export function fileProblem(file: FileLike): string | null {
  const type = file.type.toLowerCase();
  const photo = isImage(file);
  const isClip = (clipTypes as readonly string[]).includes(type);

  if (!photo && !isClip) {
    return `${file.name} cannot be added. Photographs may be JPEG, PNG, WebP or HEIC; clips MP4, WebM or MOV.`;
  }

  if (file.size === 0) {
    return `${file.name} is empty.`;
  }

  const ceiling = photo ? maxImageBytes : maxClipBytes;

  if (file.size > ceiling) {
    return `${file.name} is larger than the ${ceiling / (1024 * 1024)} MB limit.`;
  }

  return null;
}

/**
 * Adds what may be added, in the order chosen, up to the design's twenty — counting what it already
 * holds. Returns the reasons for anything turned away.
 */
export function stageFiles<T extends FileLike>(
  staged: readonly { file: T }[],
  files: readonly T[],
  savedCount: number,
  newId: () => string,
): { added: { id: string; file: T; error: null }[]; refused: string[] } {
  const room = Math.max(0, maxFilesPerDesign - savedCount - staged.length);
  const added: { id: string; file: T; error: null }[] = [];
  const refused: string[] = [];

  for (const file of files) {
    const problem = fileProblem(file);

    if (problem) {
      refused.push(problem);
    } else if (added.length >= room) {
      refused.push(
        `${file.name} was not added: a design holds at most ${maxFilesPerDesign} photographs and clips.`,
      );
    } else {
      added.push({ id: newId(), file, error: null });
    }
  }

  return { added, refused };
}

// The category choice and the request body moved to the shared core with the phone's New design
// (docs/Mobile_Redesign_Plan.md, step 10); re-exported so this page's imports stay where they were.
export {
  categoryChoices,
  designRequest,
  salePriceField,
  saleRateField,
} from '@core/catalogue/forms';
